import { useRef, useState, type PointerEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Button, Dialog, Input } from '@artosera/ui';
import {
  aimanter,
  alertesDuMur,
  placerSurAxe,
  rectangleDe,
  planAccrochageMiseAJourSchema,
  type Placement,
  type RepereAccrochage,
} from '@artosera/shared';
import {
  accrochageApi,
  usePlan,
  type DetailPlan,
  type AlertePlan,
  type NatureDocumentAccrochage,
} from '../../api/accrochage.js';
import { useExposition } from '../../api/expositions.js';
import { ouvrirUrlSignee } from '../../api/client.js';
import { dansLaPorteeCourante } from '../../api/cles.js';
import { useMutation } from '../../api/mutation.js';
import { dansLaPortee } from '../../api/cles.js';
import { galerieALaction } from '../../auth/sujet.js';
import { useShellContext } from '../shell/context.js';
import { Field } from '../Field.js';
import { EditeurMur, positionSurMur } from './EditeurMur.js';
import { PanneauOeuvres } from './PanneauOeuvres.js';
import { useHistorique } from './useHistorique.js';
import { useEnregistrementAuto, type ContenuPlan } from './useEnregistrementAuto.js';
import styles from './Accrochage.module.css';
import { Verrouillable } from '../module/LectureSeule.js';

export function PlanEditeurPage() {
  const { id = '' } = useParams();
  const { galerieSlug } = useShellContext();
  const { t } = useTranslation(['regie', 'commun']);
  const fiche = usePlan(galerieSlug, id);
  const [version, setVersion] = useState(0);
  if (fiche.isPending) return <p role="status">{t('commun:chargement')}</p>;
  if (!fiche.data) return <p role="alert">{t('commun:erreurReseau')}</p>;
  return (
    <PlanEditeur
      key={`${id}-${version}`}
      initial={fiche.data}
      recharger={async () => {
        const resultat = await fiche.refetch();
        if (resultat.isError) throw resultat.error;
        setVersion((v) => v + 1);
      }}
    />
  );
}
function PlanEditeur({
  initial,
  recharger,
}: {
  initial: DetailPlan;
  recharger: () => Promise<void>;
}) {
  const { t, i18n } = useTranslation(['regie', 'commun']);
  const { galerieSlug, galerie } = useShellContext();
  const naviguer = useNavigate();
  const historique = useHistorique<ContenuPlan>({
    nom: initial.plan.nom,
    axeCm: initial.plan.axeCm,
    etat: initial.plan.etat,
    placements: initial.plan.placements,
  });
  const contenu = historique.valeur;
  const peutEcrire = galerieALaction(galerie, 'exhibitions:write');
  const auto = useEnregistrementAuto(
    galerieSlug,
    initial.plan.id,
    initial.plan,
    contenu,
    peutEcrire,
  );
  const [murId, setMurId] = useState(initial.espace.murs[0]?._id ?? '');
  const [choisie, setChoisie] = useState<string | null>(null);
  const [annonce, setAnnonce] = useState('');
  const [erreur, setErreur] = useState('');
  const [confirmation, setConfirmation] = useState(false);
  const [document, setDocument] = useState<NatureDocumentAccrochage>('feuille-accrochage');
  const [tousLesMurs, setTousLesMurs] = useState(false);
  const [apercu, setApercu] = useState<Placement | null>(null);
  const [reperes, setReperes] = useState<RepereAccrochage[]>([]);
  const glissement = useRef<{
    placement: Placement;
    dx: number;
    dy: number;
    externe: boolean;
    pointeur: number;
  } | null>(null);
  const svgRef = useRef<SVGSVGElement | null>(null);
  const mur = initial.espace.murs.find((m) => m._id === murId);
  const exposition = useExposition(galerieSlug, initial.plan.expositionId);
  const titreExposition =
    (i18n.language === 'en'
      ? exposition.data?.exposition.titre.en
      : exposition.data?.exposition.titre.fr) || exposition.data?.exposition.titre.fr;
  const action = useMutation(
    dansLaPortee({
      mutationFn: (executer: Parameters<typeof dansLaPorteeCourante>[0], appel) => executer(appel),
    }),
  );
  const peutModifier = peutEcrire && !action.isPending;
  const bloque = auto.enCours || auto.modifie || !!auto.conflit || action.isPending;
  const oeuvre = (id: string) => initial.oeuvres.find((o) => o.id === id);
  function annoncer(p: Placement) {
    setAnnonce(
      t('accrochage.position', {
        titre: oeuvre(p.oeuvreId)?.titre ?? p.oeuvreId,
        x: p.xCm,
        y: p.yCm,
      }),
    );
  }
  function modifierPlacement(p: Placement) {
    historique.modifier({
      ...contenu,
      placements: [...contenu.placements.filter((a) => a.oeuvreId !== p.oeuvreId), p],
    });
    setChoisie(p.oeuvreId);
    annoncer(p);
    const murPlacement = initial.espace.murs.find((m) => m._id === p.murId);
    if (murPlacement) {
      const placements = [...contenu.placements.filter((a) => a.oeuvreId !== p.oeuvreId), p];
      const alertes = alertesDuMur(
        murPlacement,
        placements
          .filter((a) => a.murId === p.murId)
          .map((a) => ({
            ...rectangleDe(a, oeuvre(a.oeuvreId)?.dimensions),
            oeuvreId: a.oeuvreId,
          })),
      ).filter((a) =>
        'oeuvreIds' in a ? a.oeuvreIds.includes(p.oeuvreId) : a.oeuvreId === p.oeuvreId,
      );
      const messages = alertes.map((a) => t(`accrochage.alertes.${a.nature}`));
      const o = oeuvre(p.oeuvreId);
      if (o && (o.statut === 'retiree' || o.statut === 'archivee' || o.archive))
        messages.push(t('accrochage.alertes.oeuvre_retiree'));
      if (initial.alertes.some((a) => a.nature === 'hors_exposition' && a.oeuvreId === p.oeuvreId))
        messages.push(t('accrochage.alertes.hors_exposition'));
      setAnnonce(
        `${t('accrochage.position', { titre: o?.titre ?? p.oeuvreId, x: p.xCm, y: p.yCm })} ${messages.join(', ')}`,
      );
    }
  }
  function placementInitial(id: string): Placement | null {
    if (!mur) return null;
    const rect = rectangleDe({ xCm: 0, yCm: 0 }, oeuvre(id)?.dimensions);
    return {
      oeuvreId: id,
      murId,
      xCm: (mur.largeurCm - rect.largeur) / 2,
      yCm: placerSurAxe(rect.largeur, rect.hauteur, contenu.axeCm),
      decalageCrochetCm: 0,
    };
  }
  function commencer(
    e: PointerEvent<HTMLButtonElement | SVGGElement>,
    p: Placement,
    externe: boolean,
  ) {
    if (!peutModifier || !mur || !svgRef.current || e.button !== 0) return;
    const point = positionSurMur(svgRef.current, mur, e.clientX, e.clientY);
    const rect = rectangleDe(p, oeuvre(p.oeuvreId)?.dimensions);
    e.currentTarget.setPointerCapture?.(e.pointerId);
    glissement.current = {
      placement: p,
      dx: externe ? rect.largeur / 2 : (point?.x ?? p.xCm) - p.xCm,
      dy: externe ? rect.hauteur / 2 : (point?.y ?? p.yCm) - p.yCm,
      externe,
      pointeur: e.pointerId,
    };
    setChoisie(p.oeuvreId);
  }
  function glisser(e: PointerEvent<HTMLElement>): Placement | null {
    const drag = glissement.current;
    if (!drag || drag.pointeur !== e.pointerId || !mur || !svgRef.current) return null;
    const point = positionSurMur(svgRef.current, mur, e.clientX, e.clientY);
    if (!point) return null;
    const rect = rectangleDe(drag.placement, oeuvre(drag.placement.oeuvreId)?.dimensions);
    const aimante = aimanter(
      { ...rect, x: point.x - drag.dx, y: point.y - drag.dy },
      {
        mur,
        axeCm: contenu.axeCm,
        voisins: contenu.placements
          .filter((p) => p.murId === murId && p.oeuvreId !== drag.placement.oeuvreId)
          .map((p) => ({
            ...rectangleDe(p, oeuvre(p.oeuvreId)?.dimensions),
            oeuvreId: p.oeuvreId,
          })),
      },
    );
    const p = {
      ...drag.placement,
      murId,
      xCm: Math.round(aimante.rectangle.x * 10) / 10,
      yCm: Math.round(aimante.rectangle.y * 10) / 10,
    };
    setApercu(p);
    setReperes(aimante.reperes);
    return p;
  }
  function terminer(e: PointerEvent<HTMLElement>) {
    const drag = glissement.current;
    const p = glisser(e);
    if (drag && p && mur && svgRef.current) {
      const point = positionSurMur(svgRef.current, mur, e.clientX, e.clientY);
      if (
        !drag.externe ||
        (point &&
          point.x >= 0 &&
          point.y >= 0 &&
          point.x <= mur.largeurCm &&
          point.y <= mur.hauteurCm)
      )
        modifierPlacement(p);
    }
    glissement.current = null;
    setApercu(null);
    setReperes([]);
  }
  const placees = contenu.placements.filter(
    (p) => p.murId === murId && p.oeuvreId !== apercu?.oeuvreId,
  );
  if (apercu) placees.push(apercu);
  const selection = contenu.placements.find((p) => p.oeuvreId === choisie);
  const alertesOeuvres: AlertePlan[] = [
    ...initial.alertes.filter(
      (a) =>
        a.nature === 'hors_exposition' &&
        contenu.placements.some((p) => 'oeuvreId' in a && p.oeuvreId === a.oeuvreId),
    ),
    ...initial.oeuvres
      .filter(
        (o) =>
          (o.statut === 'retiree' || o.statut === 'archivee' || o.archive) &&
          contenu.placements.some((p) => p.oeuvreId === o.id),
      )
      .map((o) => ({ nature: 'oeuvre_retiree' as const, oeuvreId: o.id })),
  ];
  async function variante() {
    const local = { ...contenu, etat: 'brouillon' as const };
    const idCopie = await action.mutateAsync(async (appel) => {
      const courant = await accrochageApi.plan(galerieSlug, initial.plan.id, appel);
      const { plan: copie } = await accrochageApi.dupliquerPlan(
        galerieSlug,
        initial.plan.id,
        courant.plan.revision,
        appel,
      );
      await accrochageApi.enregistrerPlan(
        galerieSlug,
        copie.id,
        { ...local, nom: copie.nom, revision: copie.revision },
        appel,
      );
      return copie.id;
    });
    if (typeof idCopie === 'string') void naviguer(`/${galerieSlug}/accrochage/plans/${idCopie}`);
  }
  async function imprimer() {
    const reponse = await dansLaPorteeCourante((appel) =>
      accrochageApi.document(
        galerieSlug,
        initial.plan.id,
        document,
        i18n.language === 'en' ? 'en' : 'fr',
        document === 'feuille-accrochage' && !tousLesMurs ? murId : undefined,
        appel,
      ),
    );
    ouvrirUrlSignee(reponse.url);
  }
  let statutEnregistrement = 'accrochage.enregistre';
  if (auto.modifie) statutEnregistrement = 'accrochage.modifie';
  if (auto.enCours) statutEnregistrement = 'accrochage.enCours';
  const executer = (operation: () => Promise<unknown>) => {
    setErreur('');
    void operation().catch((e: unknown) =>
      setErreur(e instanceof Error ? e.message : t('commun:erreurReseau')),
    );
  };
  return (
    <section
      className={styles.page}
      data-ui="page"
      onPointerMove={(e) => {
        glisser(e);
      }}
      onPointerUp={terminer}
      onPointerCancel={() => {
        glissement.current = null;
        setApercu(null);
        setReperes([]);
      }}
      onKeyDown={(e) => {
        if (
          !peutModifier ||
          !(e.ctrlKey || e.metaKey) ||
          e.key.toLowerCase() !== 'z' ||
          (e.target instanceof HTMLElement && e.target.closest('input,textarea,select'))
        )
          return;
        e.preventDefault();
        if (e.shiftKey) historique.retablir();
        else historique.annuler();
      }}
    >
      <Link to={`/${galerieSlug}/accrochage?exposition=${initial.plan.expositionId}`}>
        {t('accrochage.retour')}
      </Link>
      <h1>{initial.plan.nom}</h1>
      <p>
        {titreExposition} · {t(`accrochage.etats.${contenu.etat}`)}
      </p>
      <div className={styles.actions}>
        <Field htmlFor="plan-nom" label={t('accrochage.nom')}>
          <Input
            id="plan-nom"
            disabled={!peutModifier}
            value={contenu.nom}
            maxLength={120}
            onChange={(e) => historique.modifier({ ...contenu, nom: e.target.value })}
          />
        </Field>
        <Field htmlFor="plan-axe" label={t('accrochage.axe')}>
          <Input
            id="plan-axe"
            disabled={!peutModifier}
            type="number"
            min="0.1"
            max="400"
            step="0.1"
            value={contenu.axeCm}
            onChange={(e) => historique.modifier({ ...contenu, axeCm: Number(e.target.value) })}
          />
        </Field>
        {peutEcrire && (
          <>
            <Button
              disabled={!peutModifier || !historique.peutAnnuler}
              onClick={historique.annuler}
            >
              {t('accrochage.annuler')}
            </Button>
            <Button
              disabled={!peutModifier || !historique.peutRetablir}
              onClick={historique.retablir}
            >
              {t('accrochage.retablir')}
            </Button>
            <Button
              disabled={!!auto.conflit || action.isPending}
              onClick={() =>
                historique.modifier({
                  ...contenu,
                  etat: contenu.etat === 'valide' ? 'brouillon' : 'valide',
                })
              }
            >
              {t(contenu.etat === 'valide' ? 'accrochage.brouillon' : 'accrochage.valider')}
            </Button>
          </>
        )}
      </div>
      <p role="status">{t(statutEnregistrement)}</p>
      {auto.conflit && (
        <div className={styles.panel} role="alert">
          <p>
            {t('accrochage.conflit', {
              personne: auto.conflit.modifiePar ?? t('accrochage.autreEditeur'),
              date: auto.conflit.modifieLe ?? '—',
              revision: auto.conflit.revision,
            })}
          </p>
          <div className={styles.actions}>
            <Button disabled={action.isPending} onClick={() => executer(recharger)}>
              {t('accrochage.recharger')}
            </Button>
            <Button loading={action.isPending} onClick={() => executer(variante)}>
              {t('accrochage.sauverVariante')}
            </Button>
          </div>
        </div>
      )}
      {!!auto.erreur && !auto.conflit && (
        <div role="alert">
          <p>{auto.erreur instanceof Error ? auto.erreur.message : t('commun:erreurReseau')}</p>
          <Button
            onClick={() =>
              executer(async () => {
                const validation = planAccrochageMiseAJourSchema.safeParse({
                  ...contenu,
                  revision: initial.plan.revision,
                });
                if (!validation.success) throw new Error(t('accrochage.champInvalide'));
                await auto.enregistrer();
              })
            }
          >
            {t('accrochage.reessayer')}
          </Button>
        </div>
      )}
      {erreur && <p role="alert">{erreur}</p>}
      <div className={styles.actions} role="group" aria-label={t('accrochage.murs')}>
        {initial.espace.murs.map((m) => (
          <Button
            key={m._id}
            aria-pressed={m._id === murId}
            onClick={() => {
              setMurId(m._id);
              setChoisie(null);
            }}
          >
            {m.nom}
          </Button>
        ))}
      </div>
      <p>{t('accrochage.aideClavier')}</p>
      <div className={styles.atelier}>
        <PanneauOeuvres
          oeuvres={initial.oeuvres}
          placements={contenu.placements}
          peutPlacer={peutModifier && !!mur}
          onPlacer={(id) => {
            const p = placementInitial(id);
            if (p) modifierPlacement(p);
          }}
          onGlisser={(e, id) => {
            const p = placementInitial(id);
            if (p) commencer(e, p, true);
          }}
        />
        {mur ? (
          <EditeurMur
            mur={mur}
            axeCm={contenu.axeCm}
            placements={placees}
            oeuvres={initial.oeuvres}
            alertesServeur={alertesOeuvres}
            reperes={reperes}
            svgRef={svgRef}
            peutEcrire={peutModifier}
            onChoisir={setChoisie}
            onDeplacer={modifierPlacement}
            onRetirer={(id) => {
              historique.modifier({
                ...contenu,
                placements: contenu.placements.filter((p) => p.oeuvreId !== id),
              });
              setChoisie(null);
              setAnnonce(t('accrochage.retiree', { titre: oeuvre(id)?.titre ?? id }));
            }}
            onGlisser={(e, p) => commencer(e, p, false)}
          />
        ) : (
          <p>{t('accrochage.aucunMur')}</p>
        )}
      </div>
      {selection && (
        <fieldset disabled={!peutModifier} className={styles.panel}>
          <legend>{oeuvre(selection.oeuvreId)?.titre ?? selection.oeuvreId}</legend>
          <div className={styles.grid}>
            {(['cadre', 'tailleProvisoire'] as const).map((cle) => (
              <fieldset key={cle}>
                <legend>{t(`accrochage.${cle}`)}</legend>
                <label>
                  <input
                    type="checkbox"
                    checked={!!selection[cle]}
                    onChange={(e) => {
                      const p = { ...selection };
                      if (e.target.checked) p[cle] = { largeurCm: 50, hauteurCm: 50 };
                      else delete p[cle];
                      modifierPlacement(p);
                    }}
                  />{' '}
                  {t('accrochage.renseigner')}
                </label>
                {selection[cle] &&
                  (['largeurCm', 'hauteurCm'] as const).map((dimension) => (
                    <Field
                      key={dimension}
                      htmlFor={`placement-${cle}-${dimension}`}
                      label={t(`accrochage.${dimension}`)}
                    >
                      <Input
                        id={`placement-${cle}-${dimension}`}
                        type="number"
                        min="0.1"
                        step="0.1"
                        value={selection[cle]![dimension]}
                        onChange={(e) =>
                          modifierPlacement({
                            ...selection,
                            [cle]: { ...selection[cle], [dimension]: Number(e.target.value) },
                          })
                        }
                      />
                    </Field>
                  ))}
              </fieldset>
            ))}
            <Field htmlFor="placement-crochet" label={t('accrochage.decalageCrochet')}>
              <Input
                id="placement-crochet"
                type="number"
                min="0"
                max="2000"
                step="0.1"
                value={selection.decalageCrochetCm}
                onChange={(e) =>
                  modifierPlacement({ ...selection, decalageCrochetCm: Number(e.target.value) })
                }
              />
            </Field>
          </div>
        </fieldset>
      )}
      <div className={styles.actions}>
        <Field htmlFor="plan-document" label={t('accrochage.document')}>
          <select
            id="plan-document"
            value={document}
            onChange={(e) => setDocument(e.target.value as NatureDocumentAccrochage)}
          >
            {(['feuille-accrochage', 'plan-ensemble', 'cartels', 'pointage'] as const).map((n) => (
              <option key={n} value={n}>
                {t(`accrochage.documents.${n}`)}
              </option>
            ))}
          </select>
        </Field>
        {document === 'feuille-accrochage' && (
          <label>
            <input
              type="checkbox"
              checked={tousLesMurs}
              onChange={(e) => setTousLesMurs(e.target.checked)}
            />{' '}
            {t('accrochage.tousLesMurs')}
          </label>
        )}
        <Button
          disabled={bloque || (!mur && document === 'feuille-accrochage' && !tousLesMurs)}
          onClick={() => executer(imprimer)}
        >
          {t('accrochage.imprimer')}
        </Button>
        <Verrouillable action="exhibitions:write" ouvert={peutEcrire}>
          <Button disabled={bloque || !titreExposition} onClick={() => setConfirmation(true)}>
            {t('accrochage.appliquer')}
          </Button>
        </Verrouillable>
      </div>
      <Dialog
        open={confirmation}
        onOpenChange={setConfirmation}
        title={t('accrochage.appliquer')}
        closeLabel={t('commun:actions.fermer')}
      >
        <p>{t('accrochage.confirmation', { exposition: titreExposition })}</p>
        <Button
          disabled={bloque}
          loading={action.isPending}
          onClick={() =>
            executer(async () => {
              const revision = await auto.enregistrer();
              await action.mutateAsync((appel) =>
                accrochageApi.appliquer(galerieSlug, initial.plan.id, revision, appel),
              );
              setConfirmation(false);
              await recharger();
            })
          }
        >
          {t('accrochage.confirmer')}
        </Button>
      </Dialog>
      <div aria-live="polite" aria-atomic="true" className={styles.annonce}>
        {annonce}
      </div>
    </section>
  );
}
